/*global google*/
import React, { FunctionComponent, Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@material-ui/core";
import { CustomMarker } from "./custom-marker";
import { CustomFlatRouteMarker } from "../../pages/route-details/partials/custom-flat-route-marker";
import { GoogleMap, Polyline } from "@react-google-maps/api";

export type MarkerType = "default" | "circle" | "start" | "end";

export interface IMarker {
  id: string;
  lat: number;
  lng: number;
  type?: MarkerType;
  sequenceNumber?: number;
  doNotDisplayPin?: boolean;
}

export interface ICenterOption {
  id: number;
  lat: number;
  lng: number;
}

interface ICustomMapProps {
  markers?: IMarker[];
  center?: ICenterOption;
  height?: string;
  width?: string;
  style?: string;
  shouldSort?: boolean;
  isInEditMode: boolean;
  onMapClicked?: (event: google.maps.MapMouseEvent) => void;
  onMarkerClicked?: (id: number) => void;
  onMarkerMouseUp?: (id: string, lat: number, lng: number) => void;
  zoom?: number;
  disableZoom?: boolean;
  disableInfoWindow?: boolean;
  displayPolyline?: boolean;
  doRenderCustomInfoWindow?: boolean;
  renderCustomInfoWindow?: (markerId: string) => JSX.Element | "";
  doRenderCustomPolylines?: boolean;
  renderCustomPolylines?: () => JSX.Element | "";
  disableFitBounds?: boolean;
}

export const CustomMap: FunctionComponent<ICustomMapProps> = (props) => {
  const {
    style,
    height,
    width,
    center,
    markers,
    shouldSort,
    isInEditMode,
    onMapClicked,
    onMarkerMouseUp,
    zoom,
    disableZoom,
    disableInfoWindow,
    displayPolyline,
    doRenderCustomInfoWindow,
    renderCustomInfoWindow,
    doRenderCustomPolylines,
    renderCustomPolylines = () => {
      return "";
    },
    disableFitBounds,
  } = props;
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();

  useEffect(() => {
    if (mapInstance && !disableFitBounds) {
      fitMapBounds(mapInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers]);

  const sortedMarkers: IMarker[] = useMemo(() => {
    if (!markers) {
      return [];
    }

    if (shouldSort) {
      return markers.sort(
        (marker1: IMarker, marker2: IMarker) => Number(marker1.sequenceNumber) - Number(marker2.sequenceNumber),
      );
    }

    return markers;
  }, [markers, shouldSort]);

  const fitMapBounds = useCallback(
    (map: google.maps.Map) => {
      if (sortedMarkers.length > 1) {
        const bounds = new google.maps.LatLngBounds();

        sortedMarkers.forEach((marker) => {
          bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
        });

        map.fitBounds(bounds);
      } else if (sortedMarkers.length === 1) {
        map.setCenter(new google.maps.LatLng(sortedMarkers[0].lat, sortedMarkers[0].lng));
        if (!disableZoom) {
          map.setZoom(15);
        }
      }
    },
    [disableZoom, sortedMarkers],
  );

  const onMapInit = useCallback(
    (map: google.maps.Map) => {
      setMapInstance(map);
      fitMapBounds(map);
    },
    [fitMapBounds, setMapInstance],
  );

  useEffect(() => {
    if (mapInstance) {
      mapInstance.setOptions({
        draggableCursor: isInEditMode ? "cell" : "grab",
      });
    }
  }, [mapInstance, isInEditMode]);

  const handleMarkerMouseUp = useCallback(
    (e: google.maps.MapMouseEvent, id: string) => {
      if (e.latLng && onMarkerMouseUp) {
        onMarkerMouseUp(id, e.latLng.lat(), e.latLng.lng());
      }
    },
    [onMarkerMouseUp],
  );

  const renderMarkerContent = useCallback(() => {
    return sortedMarkers
      .filter((marker) => !marker.doNotDisplayPin && (isInEditMode || marker.type !== "circle"))
      .map((marker, idx) => {
        const markerProps = {
          key: `${marker.id}_${idx}`,
          id: marker.id,
          position: {
            lat: marker.lat,
            lng: marker.lng,
          },
          disableZoom,
          draggable: isInEditMode,
          onMouseUp: (e: google.maps.MapMouseEvent) => handleMarkerMouseUp(e, marker.id.toString()),
          disableInfoWindow: disableInfoWindow,
          renderCustomInfoWindow,
        };

        switch (marker.type) {
          case "circle":
            return <CustomFlatRouteMarker {...markerProps} doRenderCustomInfoWindow={doRenderCustomInfoWindow} />;
          case "start":
          case "end":
          case "default":
          default:
            return <CustomMarker {...markerProps} type={marker.type} />;
        }
      });
  }, [
    disableInfoWindow,
    doRenderCustomInfoWindow,
    disableZoom,
    handleMarkerMouseUp,
    renderCustomInfoWindow,
    sortedMarkers,
    isInEditMode,
  ]);

  const getPolylines = useCallback(() => {
    if (!displayPolyline) {
      return "";
    }

    if (doRenderCustomPolylines) {
      return renderCustomPolylines();
    }

    const pathCoordinates = sortedMarkers?.map((marker) => {
      return { lat: marker.lat, lng: marker.lng };
    });

    return (
      <Polyline
        path={pathCoordinates}
        options={{
          strokeColor: "#000000",
          strokeOpacity: 1,
          strokeWeight: 2,
        }}
      />
    );
  }, [displayPolyline, doRenderCustomPolylines, sortedMarkers, renderCustomPolylines]);

  return (
    <Fragment>
      <Card className={style}>
        <GoogleMap
          onClick={onMapClicked}
          onLoad={onMapInit}
          mapContainerStyle={{
            height: height || "250px",
            width: width || "100%",
          }}
          zoom={zoom || 15}
          center={center}
          options={{ gestureHandling: "cooperative" }}
        >
          {getPolylines()}
          {markers && renderMarkerContent()}
        </GoogleMap>
      </Card>
    </Fragment>
  );
};
